import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { FRAMER_TRANSITIONS } from "@/lib/motion";

// ── Types ────────────────────────────────────────────────────

type ToastType = "success" | "error" | "warning" | "info";

interface ToastItem {
    id: string;
    type: ToastType;
    title: string;
    description?: string;
    action?: { label: string; onClick: () => void };
    duration: number;
}

interface ToastContextValue {
    toast: (opts: Omit<ToastItem, "id" | "duration"> & { duration?: number }) => void;
    addToast: (opts: Omit<ToastItem, "id" | "duration"> & { duration?: number }) => void;
    success: (title: string, description?: string) => void;
    error: (title: string, description?: string) => void;
    warning: (title: string, description?: string) => void;
    info: (title: string, description?: string) => void;
}

// ── Context ──────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null);

let toastCounter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<ToastItem[]>([]);

    const removeToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const addToast = useCallback(
        (opts: Omit<ToastItem, "id" | "duration"> & { duration?: number }) => {
            const id = `toast-${++toastCounter}`;
            const duration = opts.duration ?? 4000;
            const item: ToastItem = { ...opts, id, duration };

            setToasts((prev) => [...prev.slice(-2), item]); // Max 3

            if (duration > 0) {
                setTimeout(() => removeToast(id), duration);
            }
        },
        [removeToast],
    );

    const ctx = useMemo<ToastContextValue>(() => ({
        toast: addToast,
        addToast,
        success: (title, description) => addToast({ type: "success", title, description }),
        error: (title, description) =>
            addToast({ type: "error", title, description, duration: 6000 }),
        warning: (title, description) => addToast({ type: "warning", title, description }),
        info: (title, description) => addToast({ type: "info", title, description }),
    }), [addToast]);

    return (
        <ToastContext.Provider value={ctx}>
            {children}

            {/* Toast container */}
            <div
                className="fixed bottom-4 right-4 z-[60] flex flex-col-reverse gap-2 pointer-events-none"
                aria-live="polite"
                aria-label="Benachrichtigungen"
            >
                <AnimatePresence mode="popLayout">
                    {toasts.map((t) => (
                        <ToastCard key={t.id} toast={t} onDismiss={() => removeToast(t.id)} />
                    ))}
                </AnimatePresence>
            </div>
        </ToastContext.Provider>
    );
}

export function useToast(): ToastContextValue {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error("useToast must be used within ToastProvider");
    return ctx;
}

// ── Toast Card ───────────────────────────────────────────────

const ICON_MAP: Record<ToastType, typeof CheckCircle2> = {
    success: CheckCircle2,
    error: XCircle,
    warning: AlertTriangle,
    info: Info,
};

const COLOR_MAP: Record<ToastType, { icon: string; border: string }> = {
    success: { icon: "text-green-500", border: "border-l-green-500" },
    error: { icon: "text-red-500", border: "border-l-red-500" },
    warning: { icon: "text-yellow-500", border: "border-l-yellow-500" },
    info: { icon: "text-blue-500", border: "border-l-blue-500" },
};

function ToastCard({ toast, onDismiss }: { toast: ToastItem; onDismiss: () => void }) {
    const Icon = ICON_MAP[toast.type];
    const colors = COLOR_MAP[toast.type];

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={FRAMER_TRANSITIONS.component}
            className={cn(
                "pointer-events-auto relative flex w-80 items-start gap-3 rounded-lg border border-neutral-200 dark:border-neutral-600 border-l-4 bg-white dark:bg-neutral-800 px-4 py-3 shadow-elevated overflow-hidden",
                colors.border,
            )}
            role="alert"
        >
            <Icon className={cn("h-5 w-5 mt-0.5 shrink-0", colors.icon)} />
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-800 dark:text-neutral-200">
                    {toast.title}
                </p>
                {toast.description && (
                    <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                        {toast.description}
                    </p>
                )}
                {toast.action && (
                    <button
                        type="button"
                        onClick={() => {
                            toast.action?.onClick();
                            onDismiss();
                        }}
                        className="mt-1 text-xs font-medium text-brand-600 hover:text-brand-700"
                    >
                        {toast.action.label}
                    </button>
                )}
            </div>
            <button
                type="button"
                onClick={onDismiss}
                className="rounded p-0.5 text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                aria-label="Schließen"
            >
                <X className="h-3.5 w-3.5" />
            </button>
            {/* Auto-dismiss progress bar */}
            {toast.duration > 0 && (
                <div
                    className="absolute bottom-0 left-0 h-0.5 bg-current opacity-15"
                    style={{
                        animation: `toast-progress ${toast.duration}ms linear forwards`,
                    }}
                />
            )}
        </motion.div>
    );
}
